<template>
  <Modal
    :id="`${uid}-modal`"
    :visible="visible"
    class="cargo-insurance-cancellation-modal"
    :header="
      t('common.cancelParam', { param: t('cargoInsurance.cargoInsurance') })
    "
  >
    <p id="cargo-insurance-cancellation-instructions" class="section-header">
      {{
        t('common.confirmCancelationParams', {
          param: t('cargoInsurance.cargoInsurance'),
        })
      }}
    </p>

    <form
      id="cargo-insurance-cancellation"
      aria-labelledby="cargo-insurance-cancellation-instructions"
      class="flex flex-column flex-gap-1"
      @submit.prevent
    >
      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        :input-id="`${uid}-reason`"
        :label="t('cargoInsurance.cancellation.reason')"
        :errors="v$.cancellationReason.$errors"
      >
        <RygenDropdown
          :id="inputId"
          v-model="cargoInsuranceCancellationRequest.cancellationReason"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :options="cancellationReasons"
          :placeholder="t('cargoInsurance.cancellation.reason')"
          option-value="id"
          option-label="description"
          append-to="body"
          filter
        />
      </InputWrapper>

      <InputWrapper
        v-if="v$.cancellationDetails.required"
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        :input-id="`${uid}-cancellation-details`"
        :label="t('cargoInsurance.cancellation.details')"
        :errors="v$.cancellationDetails.$errors"
      >
        <Textarea
          :id="inputId"
          v-model="cargoInsuranceCancellationRequest.cancellationDetails"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          fluid
          :placeholder="t('cargoInsurance.cancellation.details')"
        />
      </InputWrapper>
    </form>

    <template #footer>
      <RygenButton
        mode="tertiary"
        :label="
          t('common.dontCancelParam', {
            param: t('cargoInsurance.cargoInsurance'),
          })
        "
        @click="visible = false"
      />

      <RygenButton
        :label="
          t('common.cancelParam', {
            param: t('cargoInsurance.cargoInsurance'),
          })
        "
        :severity="Severity.DANGER"
        icon="pi pi-times"
        @click="onConfirm"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { computed, ref, Ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  required,
  useUid,
  useValidations,
  ValidationRules,
} from '@rygen/rygen-ui/composables';
import { Severity } from '@rygen/rygen-ui/model';

import {
  CargoInsuranceCancellationReason,
  CargoInsuranceCancellationRequest,
} from '@/model';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';
import Textarea from 'primevue/textarea';

const visible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const emit = defineEmits<{
  (
    event: 'cancel-cargo-insurance',
    cargoInsuranceCancellationRequest: CargoInsuranceCancellationRequest,
  ): void;
}>();

const { t } = useI18n();
const uid = useUid('cargo-insurance-cancellation-modal').uid;

const cancellationReasons: Ref<CargoInsuranceCancellationReason[]> = ref([
  { id: 'CANASD', description: 'Assured Changed' },
  { id: 'CANNLN', description: 'Insurance Certificate no longer needed' },
  { id: 'CANPIE', description: 'Certificate purchased in error' },
  { id: 'CANCHP', description: 'Cheaper insurance found elsewhere' },
  { id: 'CANVAL', description: 'Insured value changed' },
  { id: 'CANREQ', description: 'Insurance was not required by the shipper' },
  { id: 'CANCOP', description: 'Shipment lost to competition' },
  { id: 'CANREP', description: 'Origin/Destination/Commodity Changed' },
  { id: 'CANOTH', description: 'Other' },
]);

const cargoInsuranceCancellationRequest: Ref<
  Partial<CargoInsuranceCancellationRequest>
> = ref({});

const rules = computed(() => {
  const rules: ValidationRules = {
    cancellationReason: {
      required: required(t('cargoInsurance.cancellation.reason')),
    },
    cancellationDetails: {},
  };

  if (cargoInsuranceCancellationRequest.value.cancellationReason === 'CANOTH') {
    rules.cancellationDetails = {
      required: required(t('cargoInsurance.cancellation.details')),
    };
  }

  return rules;
});

const { v$, validate } = useValidations(
  cargoInsuranceCancellationRequest,
  rules,
  uid,
);

watch(visible, () => {
  cargoInsuranceCancellationRequest.value = {};
  v$.value.$reset();
});

watch(
  () => cargoInsuranceCancellationRequest.value.cancellationReason,
  () => (cargoInsuranceCancellationRequest.value.cancellationDetails = ''),
);

const onConfirm = async () => {
  const isValid = await validate();
  if (!isValid) {
    console.warn('Validation failed', v$);
    return;
  }

  emit(
    'cancel-cargo-insurance',
    new CargoInsuranceCancellationRequest(
      cargoInsuranceCancellationRequest.value,
    ),
  );
  visible.value = false;
};
</script>
